import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { checkMot } from '../../../../ressources/functionJS/checkMot';
import { emptyString } from '../../../../ressources/functionJS/emptyString';
import { jsDictionaryEquals } from '../../../../ressources/functionJS/jsDictionaryEquals';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import { estDansArborescenceProjets } from '../../../../services/reducteurs/interfaceClient';
import { defaultColors } from '../../../UI/ColorPlotly';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import CustomLegend from '../LabelEventGraph/components/CustomLegend';

// Id du projet racine qui active la légende custom React (au lieu de la légende native Plotly).
// Même mécanisme que estDansArborescenceProjets(..., 1084, ...) utilisé dans BibliothequePlotly.
const PROJET_LEGENDE_CUSTOM = 1084;

/**
 * Convertit un timestamp en date formatée
 * @param {number} timestamp - Timestamp en ms (ou en secondes si unix: true)
 * @param {string} format - Format souhaité
 * @param {object} options - Options supplémentaires
 * @returns {string}
 */
function formatTimestamp(
  timestamp,
  format = 'DD/MM/YYYY',
  { unix = true, locale = 'fr-FR' } = {}
) {
  const date = new Date(unix ? timestamp * 1000 : timestamp);

  const pad = (n) => String(n).padStart(2, '0');

  const getISOWeek = (d) => {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4);
    return (
      1 +
      Math.round(
        ((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
      )
    );
  };

  const tokens = {
    dddd: date.toLocaleDateString(locale, { weekday: 'long' }),
    ddd: date.toLocaleDateString(locale, { weekday: 'short' }),
    MMMM: date.toLocaleDateString(locale, { month: 'long' }),
    MMM: date.toLocaleDateString(locale, { month: 'short' }),
    YYYY: date.getFullYear(),
    YY: String(date.getFullYear()).slice(-2),
    MM: pad(date.getMonth() + 1),
    DD: pad(date.getDate()),
    HH: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
    WW: pad(getISOWeek(date)), // S02, S22...
    W: getISOWeek(date), // S2, S22...
  };

  return Object.entries(tokens).reduce(
    (result, [token, value]) => result.replaceAll(token, value),
    format
  );
  // formatTimestamp(ts, 'DD/MM/YYYY')           // 25/03/2025
  // formatTimestamp(ts, 'DD MMM YYYY')          // 25 Mar. 2025
  // formatTimestamp(ts, 'YYYY-MM-DD')           // 2025-03-25
  // formatTimestamp(ts, 'DD/MM/YYYY HH:mm')     // 25/03/2025 14:30
  // formatTimestamp(ts, 'dddd DD MMMM YYYY')    // Mardi 25 Mars 2025
}

export const convertiUtsToDate = (arrayOfUts) => {
  return arrayOfUts.map((unix) => {
    if (isNaN(unix)) {
      return null;
    }
    let unixToconvert = Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(unix * 1000);
    unixToconvert = unixToconvert.replaceAll('/', '-').replace(',', '');
    const time = unixToconvert.split(' ')[1];
    let splitDate = unixToconvert.split(' ')[0].split('-');
    let date = splitDate[2] + '-' + splitDate[1] + '-' + splitDate[0];
    return date + ' ' + time;
  });
};
export const formatTextUniteArray = (valeurs, format) => {
  let calculValeurs = [...formatUniteArray(valeurs, format, true)];
  let res = [];
  let newFormat = format;
  if (format.split('2').length > 1) {
    newFormat = format.split('2')[1];
  }
  // boucle pour convertir chaque valeur dans le format en entrée
  for (let i = 0; i < calculValeurs.length; i++) {
    res.push(formatUnite(calculValeurs[i], format) + ' ' + newFormat);
  }
  return res;
};

export const conversions = {
  // Composés
  'jj:hh:mm:ss': (val) => val / (24 * 60 * 60),
  'hh:mm:ss': (val) => val / (60 * 60),
  'mm:ss': (val) => val / 60,
  'mm2hh:mm': (val) => val / 60,
  'ss2hh:mm:ss': (val) => val / (60 * 60),
  'ss2mm:ss': (val) => val / 60,

  // Simples
  jj: (val) => (val / (24 * 60 * 60)).toFixed(2),
  hh: (val) => (val / (60 * 60)).toFixed(2),
  // "mm": val => (val / 60).toFixed(2), // todo: attention confusion avec millimètre
  ss: (val) => val,

  // Jour vers
  jj2hh: (val) => (val * 24).toFixed(2),
  jj2mm: (val) => (val * 24 * 60).toFixed(2),
  jj2ss: (val) => (val * 24 * 60 * 60).toFixed(2),

  // Heure vers
  hh2jj: (val) => (val / 24).toFixed(2),
  hh2mm: (val) => (val * 60).toFixed(2),
  hh2ss: (val) => (val * 60 * 60).toFixed(2),

  // Minute vers
  mm2jj: (val) => (val / (24 * 60)).toFixed(2),
  mm2hh: (val) => (val / 60).toFixed(2),
  mm2ss: (val) => (val * 60).toFixed(2),

  // Seconde vers
  ss2jj: (val) => (val / (24 * 60 * 60)).toFixed(2),
  ss2hh: (val) => (val / (60 * 60)).toFixed(2),
  ss2mm: (val) => (val / 60).toFixed(2),
};

export const conversionsAffichageTexte = {
  // Composés
  'jj:hh:mm:ss': (val) =>
    `${Math.floor(val / (24 * 60 * 60))}:${Math.floor(
      (val % (24 * 60 * 60)) / (60 * 60)
    )}:${Math.floor((val % (60 * 60)) / 60)}:${val % 60}`,
  'hh:mm:ss': (val) =>
    `${Math.floor(val / (60 * 60))}:${Math.floor((val % (60 * 60)) / 60)}:${
      val % 60
    }`,
  'mm:ss': (val) => `${Math.floor(val / 60)}:${val % 60}`,
  'mm2hh:mm': (val) => `${Math.floor(val / 60)}:${val % 60}`,
  'ss2hh:mm:ss': (val) =>
    `${Math.floor(val / (60 * 60))}:${Math.floor((val % (60 * 60)) / 60)}:${
      val % 60
    }`,
  'ss2mm:ss': (val) => `${Math.floor(val / 60)}:${val % 60}`,

  // Simples
  jj: (val) => (val / (24 * 60 * 60)).toFixed(2),
  hh: (val) => (val / (60 * 60)).toFixed(2),
  // "mm": val => (val / 60).toFixed(2), // todo: attention confusion avec millimètre
  ss: (val) => val,

  // Jour vers
  jj2hh: (val) => (val * 24).toFixed(2),
  jj2mm: (val) => (val * 24 * 60).toFixed(2),
  jj2ss: (val) => (val * 24 * 60 * 60).toFixed(2),

  // Heure vers
  hh2jj: (val) => (val / 24).toFixed(2),
  hh2mm: (val) => (val * 60).toFixed(2),
  hh2ss: (val) => (val * 60 * 60).toFixed(2),

  // Minute vers
  mm2jj: (val) => (val / (24 * 60)).toFixed(2),
  mm2hh: (val) => (val / 60).toFixed(2),
  mm2ss: (val) => (val * 60).toFixed(2),

  // Seconde vers
  ss2jj: (val) => (val / (24 * 60 * 60)).toFixed(2),
  ss2hh: (val) => (val / (60 * 60)).toFixed(2),
  ss2mm: (val) => (val / 60).toFixed(2),
};

export const formatUnite = (valeur, format) => {
  if (
    Number.isInteger(valeur) &&
    conversionsAffichageTexte.hasOwnProperty(format)
  ) {
    return conversionsAffichageTexte[format](valeur);
  } else {
    return valeur;
  }
};

// L'API renvoie parfois des couleurs hexa sans '#' (ex: "0096C7") — valide pour Plotly
// (tinycolor) mais pas pour un attribut SVG natif (stroke/fill) utilisé par la légende custom.
const normalizeHexColor = (color) => {
  if (typeof color !== 'string') return color;
  const trimmed = color.trim();
  return /^([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)
    ? `#${trimmed}`
    : trimmed;
};

export const formatUniteArray = (valeurs, format, affichageText) => {
  let res = [];

  let formatAxe = format;
  if (typeof format === 'string' && format.includes('2') && affichageText) {
    formatAxe = format.split('2')[0];
  }
  // vérifie si le format est valide
  const isValidFormat = formatAxe in conversions;

  // boucle pour convertir chaque valeur dans le format en entrée
  for (let i = 0; i < valeurs.length; i++) {
    let calculValeur = valeurs[i];
    if (Number.isInteger(calculValeur) && isValidFormat) {
      res.push(conversions[formatAxe](calculValeur));
    } else {
      res.push(calculValeur);
    }
  }
  return res;
};

const GrapheXT = ({
  configuration,
  donnees,
  seuils,
  viewobj_json_content,
  switchAfficherModifierIntervalle,
  donneesGraphe,
  dimensionWidth,
  dimensionHeight,
  minimaliste,
}) => {
  // Split state into multiple useState hooks
  const [afficherRangeSlider, setAfficherRangeSlider] = useState(false);
  const [afficherManuelMode, setAfficherManuelMode] = useState(false);
  const [afficherModeBar, setAfficherModeBar] = useState(false);
  const [afficherY2, setAfficherY2] = useState(false);
  const [hiddenGroups, setHiddenGroups] = useState(() => new Set());
  const containerRef = useRef(null);
  const [legendWidth, setLegendWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(undefined);

  const project_id_url = useSelector(
    (state) => state.interfaceClient.project_id_url
  );
  const arborescence_projets = useSelector(
    (state) => state.interfaceClient.arborescence_projets
  );
  const useCustomLegend = estDansArborescenceProjets(
    arborescence_projets,
    PROJET_LEGENDE_CUSTOM,
    project_id_url
  );

  const toggleLegendGroup = useCallback((id) => {
    setHiddenGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const isolateLegendGroup = useCallback((id, allIds) => {
    setHiddenGroups((prev) => {
      const othersAllHidden = allIds
        .filter((i) => i !== id)
        .every((i) => prev.has(i));
      return othersAllHidden
        ? new Set()
        : new Set(allIds.filter((i) => i !== id));
    });
  }, []);

  const handleRangeSliderToggle = () => {
    setAfficherRangeSlider((prev) => !prev);
  };

  const handleModeBarToggle = () => {
    setAfficherManuelMode(true);
    setAfficherModeBar((prev) => !prev);
  };

  const handleY2Toggle = (have_y2_curves) => {
    if (!have_y2_curves) {
      setAfficherY2((prev) => !prev);
    }
  };

  // Vérifié si le graphe est périodique/'cumulé'
  const isPeriodic = () => {
    return configuration.cumul_interval;
  };

  // Layout and data processing
  let layout = {};
  let data = [];
  let have_y2_curves = false;
  const legendItems = []; // items de la légende custom React (utilisé si useCustomLegend)

  // Format de l'affichage des dates pour le graphes périodiques dit 'cumulés'
  // Format custom de la fonction 'formatTimestamp()'
  let dateFormat = '%d/%m/%Y %H:%M';
  switch (configuration.cumul_interval) {
    case 'heure':
      dateFormat = 'HH:00';
      break;
    case 'jour':
      dateFormat = 'DD/MM/YYYY';
      break;
    case 'semaine':
      dateFormat = 'SWW';
      break;
    case 'mois':
      dateFormat = 'MM/YYYY';
      break;
    case 'annee':
      dateFormat = 'YYYY';
      break;
    default:
      dateFormat = '%d/%m/%Y %H:%M';
  }

  if (configuration !== undefined && donnees !== undefined) {
    // Layout configuration
    layout = {
      font: { size: 14 },
      title: {
        text: configuration.title + '<br>' + configuration.subtitle,
        font: { color: '#3c3935' },
      },
      barmode: configuration.barmode,
      xaxis: {
        ...configuration.xaxis,
        tickformat: dateFormat,
        range:
          configuration.graph_type_id === glossaire_type_de_graphe.graphe_x_t &&
          configuration.xaxis.range !== undefined &&
          configuration.xaxis.range.length > 1 &&
          configuration.xaxis.range[0] !== null &&
          configuration.xaxis.range[1] !== null
            ? convertiUtsToDate(configuration.xaxis.range)
            : configuration.xaxis.range,
        autorange:
          configuration.xaxis.range === undefined ||
          configuration.xaxis.range.length === 0 ||
          configuration.xaxis.range[0] === null ||
          configuration.xaxis.range[1] === null,
        showgrid: configuration.showgrid,
        autoscale: !jsDictionaryEquals(configuration.xaxis.range, [null, null]),
        rangeslider:
          viewobj_json_content === undefined && viewobj_json_content !== null
            ? { visible: afficherRangeSlider }
            : viewobj_json_content !== null &&
                viewobj_json_content.showRangeslider === true
              ? { visible: afficherRangeSlider }
              : undefined,
      },
      yaxis: {
        ...configuration.yaxis,
        showgrid: configuration.showgrid,
        autoscale: !jsDictionaryEquals(configuration.yaxis.range, [null, null]),
      },
      yaxis2: {
        ...configuration.yaxis2,
        showgrid: configuration.showgrid,
        autoscale:
          configuration.yaxis2 !== undefined &&
          !jsDictionaryEquals(configuration.yaxis2.range, [null, null]),
        overlaying: 'y',
        side: 'right',
      },
      ...(viewobj_json_content === undefined || viewobj_json_content === null
        ? {}
        : viewobj_json_content.layout),
    };

    // Si le graphe est de type périodique, affichage discret des dates par 'category'
    if (isPeriodic()) {
      layout.xaxis.type = 'category';
    }

    // Process data curves
    for (
      let indSetDonnees = 0;
      indSetDonnees < donnees.length;
      indSetDonnees++
    ) {
      if (donnees[indSetDonnees]['yaxis'] === 'y2') {
        have_y2_curves = true;
      }

      // Couleur toujours résolue explicitement (jamais laissée au colorway auto de Plotly) :
      // la légende custom doit afficher exactement la même couleur que la courbe tracée.
      const resolvedCurveColor = normalizeHexColor(
        donnees[indSetDonnees]['color'] ||
          defaultColors[indSetDonnees % defaultColors.length]
      );
      const color_curve = {
        marker: {
          ...(donnees[indSetDonnees].marker || {}),
          color: resolvedCurveColor,
        },
        line: {
          ...(donnees[indSetDonnees].line || {}),
          color: resolvedCurveColor,
        },
      };
      let text = [];
      if (
        Object.keys(conversionsAffichageTexte).indexOf(
          donnees[indSetDonnees].engineering_unit > -1
        )
      ) {
        text = [
          ...formatTextUniteArray(
            donnees[indSetDonnees].y,
            donnees[indSetDonnees].engineering_unit
          ),
        ];
      } else {
        for (
          let indDonnee = 0;
          indDonnee < donnees[indSetDonnees].x.length;
          indDonnee++
        ) {
          // mise en forme du nom : name || sensor_name[prefix || position_in_sensor]
          const name = donnees[indSetDonnees].sensor_name;
          const valeurY =
            (!emptyString(donnees[indSetDonnees].name)
              ? donnees[indSetDonnees].name
              : !emptyString(donnees[indSetDonnees].prefix)
                ? donnees[indSetDonnees].prefix
                : '[' + donnees[indSetDonnees].position_in_sensor + ']') +
            ': ' +
            donnees[indSetDonnees].y[indDonnee] +
            ' ' +
            donnees[indSetDonnees].engineering_unit;
          // const valeurX = layout.xaxis.title+": "+utsToDate(donnees[indSetDonnees].x[indDonnee]);

          // mise en forme de l'info bulle pour chaque donnée
          let infoBulle = [
            name,
            valeurY,
            // valeurX,
          ];
          text.push(infoBulle.join('<br>'));
        }
      }
      //converti l'axe des uts en date format plotly (exemple: "2013-10-04 22:23:00")
      // const timeAxis = convertiUtsToDate(donnees[indSetDonnees].x);
      let timeAxis = [];
      if (isPeriodic()) {
        timeAxis = donnees[indSetDonnees].x.map((t) =>
          formatTimestamp(t, dateFormat)
        );
      } else {
        timeAxis = convertiUtsToDate(donnees[indSetDonnees].x);
      }

      const valeurAxis = formatUniteArray(
        donnees[indSetDonnees].y,
        donnees[indSetDonnees].engineering_unit
      );

      let courbe = {
        ...donnees[indSetDonnees],
        x: timeAxis,
        y: valeurAxis,
        text: text,
        hovertemplate: '%{text}<extra></extra>',
        ...color_curve,
      };

      //affiche le nom du capteur [prefix || num composante] si name ===""
      if (donnees[indSetDonnees].name === '') {
        courbe.name =
          donnees[indSetDonnees].sensor_name +
          '[' +
          (donnees[indSetDonnees].prefix !== ''
            ? donnees[indSetDonnees].prefix
            : donnees[indSetDonnees].position_in_sensor) +
          ']';
      }

      if (donnees[indSetDonnees].type === 'bar') {
        courbe.type = 'bar';
      } else {
        courbe.type = 'scatter';
      }

      //cas où on utilise la bar d'outil pour changer le type d'affichage
      if (afficherManuelMode === true) {
        if (afficherModeBar === true) {
          courbe.type = 'bar';
        } else {
          courbe.type = 'scatter';
        }
      }

      // Légende custom React : la légende native Plotly est masquée sur cette courbe
      // et remplacée par un item géré côté React (couleur/trait/visibilité).
      if (useCustomLegend) {
        const legendGroup = `signal:${indSetDonnees}`;
        courbe.legendgroup = legendGroup;
        courbe.showlegend = false;
        courbe.visible = hiddenGroups.has(legendGroup) ? false : true;
        legendItems.push({
          id: legendGroup,
          name: courbe.name,
          type: 'signal',
          color: resolvedCurveColor,
          dash: courbe.line?.dash || 'solid',
        });
      }

      data.push(courbe);
    }

    // Process threshold data
    for (let indSetSeuils = 0; indSetSeuils < seuils.length; indSetSeuils++) {
      //spread des config générales du groupes de seuils sur la liste des seuils
      if (
        seuils[indSetSeuils].show_alarms === undefined ||
        parseInt(seuils[indSetSeuils].show_alarms) === 1
      ) {
        let afficherDansLegende = true;
        for (
          let indSeuil = 0;
          indSeuil < seuils[indSetSeuils].data.length;
          indSeuil++
        ) {
          if (
            !(
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].x, null) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].x, [
                null,
              ]) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].x, [
                null,
                null,
              ]) ||
              jsDictionaryEquals(
                seuils[indSetSeuils].data[indSeuil].x,
                undefined
              ) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].x, '') ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].y, null) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].y, [
                null,
              ]) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].y, [
                null,
                null,
              ]) ||
              jsDictionaryEquals(
                seuils[indSetSeuils].data[indSeuil].y,
                undefined
              ) ||
              jsDictionaryEquals(seuils[indSetSeuils].data[indSeuil].y, '')
            )
          ) {
            let text = [];
            // mise en forme du nom : name || sensor_name[prefix || position_in_sensor]
            const sensorName =
              '[' +
              checkMot('alarme') +
              ']' +
              !emptyString(seuils[indSetSeuils].name)
                ? seuils[indSetSeuils].name
                : seuils[indSetSeuils].sensor_name;
            if (seuils[indSetSeuils].data[indSeuil].y !== undefined) {
              for (
                let indValeur = 0;
                indValeur < seuils[indSetSeuils].data[indSeuil].y.length;
                indValeur++
              ) {
                const seuil =
                  (!emptyString(seuils[indSetSeuils].prefix)
                    ? seuils[indSetSeuils].prefix
                    : '[' + seuils[indSetSeuils].position_in_sensor + ']') +
                  ': ' +
                  seuils[indSetSeuils].data[indSeuil].y[indValeur] +
                  ' ' +
                  seuils[indSetSeuils].engineering_unit;
                // mise en forme de l'info bulle pour chaque donnée
                let infoBulle = [sensorName, seuil];
                text.push(infoBulle.join('<br>'));
              }
            }
            //distribue la configuration générale de la courbe de seuils dans les données
            let courbe = {
              ...seuils[indSetSeuils],
              name: seuils[indSetSeuils].sensor_name,
              ...seuils[indSetSeuils].data[indSeuil],
              x: convertiUtsToDate(seuils[indSetSeuils].data[indSeuil].x),
              y: formatUniteArray(
                seuils[indSetSeuils].data[indSeuil].y,
                seuils[indSetSeuils].data[indSeuil].engineering_unit
              ),
            };
            let linecolor;
            switch (
              courbe.line_color.startsWith('High ') ||
              courbe.line_color.startsWith('Low ')
                ? courbe.line_color.split(' ')[1].toLowerCase()
                : courbe.line_color
            ) {
              case 'black':
                linecolor = 'black';
                break;
              case 'red':
                linecolor = '#f70606';
                break;
              case 'amber':
                linecolor = '#ffa233';
                break;
              case 'blue':
                linecolor = '#3390ff';
                break;
              default:
                linecolor = 'green';
                break;
            }
            const alarmLegendGroup = seuils[indSetSeuils].sensor_name;
            data.push({
              ...courbe,
              type: 'scatter',
              line: {
                ...courbe.line,
                color: linecolor,
                dash: 'solid',
              },
              name:
                '[' +
                checkMot('alarme') +
                '] ' +
                seuils[indSetSeuils].sensor_name,
              legendgroup: alarmLegendGroup,
              showlegend: useCustomLegend ? false : afficherDansLegende,
              visible: useCustomLegend
                ? hiddenGroups.has(alarmLegendGroup)
                  ? false
                  : true
                : undefined,
              text: text,
              hovertemplate: '%{text}<extra></extra>',
            });
            if (useCustomLegend && afficherDansLegende) {
              legendItems.push({
                id: alarmLegendGroup,
                name:
                  '[' +
                  checkMot('alarme') +
                  '] ' +
                  seuils[indSetSeuils].sensor_name,
                type: 'alarm',
                color: linecolor,
                dash: 'solid',
              });
            }
            afficherDansLegende = false; //affiche que la première légende groupé
          }
        }
      }
    }
  }

  // Handle Y2 axis display
  if (afficherY2 === true && !have_y2_curves) {
    layout['legend'] = {
      x: 1.17,
      xanchor: 'right',
      y: 1,
    };
    layout['yaxis'] = {
      ...layout['yaxis'],
      rangemode: 'tozero',
    };
    layout['yaxis2'] = {
      ...layout['yaxis'],
      title: '',
      titlefont: { color: 'rgb(166,22,63)' },
      tickfont: { color: 'rgb(166,22,63)' },
      overlaying: 'y',
      side: 'right',
      showlegend: false, //todo: hide legende
      gridcolor: 'rgba(166,22,63,0.3)',
      gridwidth: 1,
      zerolinecolor: 'rgba(166,22,63,0.7)',
      zerolinewidth: 2,
      hovermode: false,
    };
    let seuils_list = [];
    for (let seuilGroup of seuils) {
      //récupère la liste des seuils pour trouver les extremums
      for (let seuil_couleur of seuilGroup.data) {
        seuils_list.push(...seuil_couleur.y);
      }
    }
    data.push({
      //todo: hide text bulle
      x: [
        data[0].x[parseInt(data[0].x.length / 2)],
        data[0].x[parseInt(data[0].x.length / 2) + 1],
      ],
      y: [
        Math.min(...data[0].y, ...seuils_list),
        Math.max(...data[0].y, ...seuils_list),
      ],
      name: '',
      showlegend: false,
      yaxis: 'y2',
      type: data[0].type,
      mode: 'none',
      hoverinfo: 'none',
      opacity: 0,
    });
  }
  //booleen pour trier la légende dans l'ordre alphabétique
  const sensor_name_sort = false;

  // Légende custom React : masque la légende native Plotly (les traces portent déjà showlegend:false).
  if (useCustomLegend) {
    layout.showlegend = false;
  }

  const legendItemIds = useMemo(
    () => legendItems.map((item) => item.id),
    [legendItems]
  );

  // Mesure l'espace réellement disponible (le conteneur) pour en déduire, en le
  // soustrayant à la largeur de la légende, la largeur restante à allouer à Plotly.
  useLayoutEffect(() => {
    if (!useCustomLegend) {
      setContainerWidth(undefined);
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const update = () => setContainerWidth(container.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  }, [useCustomLegend]);

  useLayoutEffect(() => {
    if (!useCustomLegend) {
      setLegendWidth(0);
      return;
    }
    const legendEl = containerRef.current?.querySelector('.leg-custom-legend');
    if (!legendEl) return;
    const update = () => setLegendWidth(legendEl.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(legendEl);
    return () => ro.disconnect();
  }, [useCustomLegend, legendItems.length]);

  const availableWidth = containerWidth ?? dimensionWidth;
  const plotDimensionWidth =
    useCustomLegend && availableWidth !== undefined && legendWidth > 0
      ? Math.max(200, availableWidth - legendWidth)
      : dimensionWidth;

  const plotly = (
    <BibliothequePlotly
      sensor_name_sort={sensor_name_sort}
      data={data}
      layout={layout}
      ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
      donneesGraphe={donneesGraphe}
      dimensionWidth={plotDimensionWidth}
      dimensionHeight={dimensionHeight}
      minimaliste={minimaliste}
      viewobj_json_content={viewobj_json_content}
      afficherRangeSlider={handleRangeSliderToggle}
      afficherModeBar={handleModeBarToggle}
      afficherY2={have_y2_curves ? null : () => handleY2Toggle(have_y2_curves)}
      modeBar={afficherModeBar}
    />
  );

  if (!useCustomLegend) {
    return plotly;
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: dimensionWidth !== undefined ? dimensionWidth : '100%',
      }}
    >
      {plotly}
      <CustomLegend
        items={legendItems}
        hiddenGroups={hiddenGroups}
        onToggle={toggleLegendGroup}
        onIsolate={(id) => isolateLegendGroup(id, legendItemIds)}
      />
    </div>
  );
};

export default GrapheXT;
